import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Nav from "./Navigation.js";
import { getTokenInternal } from "./useToken";

const CATEGORY_LABELS = {
  items: "Items",
  types: "Types",
  maps: "Maps",
  maps_name: "Map Names",
  event: "Events",
};

const CATEGORY_LIST_URLS = {
  items: "/api/items",
  types: "/api/types",
  maps: "/api/maps",
  maps_name: "/api/maps_name",
  event: "/api/events",
};

export default function ReviewTranslations() {
  const [token, setToken] = useState();
  const [pending, setPending] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [accountsById, setAccountsById] = useState({});
  const [englishNamesByCategory, setEnglishNamesByCategory] = useState({});
  const [loading, setLoading] = useState(true);
  const [editValues, setEditValues] = useState({});
  const [actionError, setActionError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const init = async () => {
      const fetchedToken = await getTokenInternal();
      if (
        !fetchedToken ||
        !["owner", "management"].includes(fetchedToken.account.role)
      ) {
        navigate("/");
        return;
      }
      setToken(fetchedToken);
    };
    init();
  }, []);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_HOST}/api/languages`)
      .then((res) => (res.ok ? res.json() : []))
      .then(setLanguages)
      .catch((err) => console.error("Error fetching languages:", err.message));

    getTokenInternal().then((t) =>
      fetch(`${process.env.REACT_APP_API_HOST}/api/accounts`, {
        headers: t ? { Authorization: `Bearer ${t.access_token}` } : {},
      })
        .then((res) => (res.ok ? res.json() : []))
        .then((accounts) => {
          const byId = {};
          accounts.forEach((account) => {
            byId[account.id] = account.username;
          });
          setAccountsById(byId);
        })
        .catch((err) => console.error("Error fetching accounts:", err.message))
    );

    Promise.all(
      Object.entries(CATEGORY_LIST_URLS).map(([category, url]) =>
        fetch(`${process.env.REACT_APP_API_HOST}${url}`)
          .then((res) => (res.ok ? res.json() : []))
          .then((list) => [category, list])
      )
    )
      .then((results) => {
        const namesByCategory = {};
        results.forEach(([category, list]) => {
          const byId = {};
          list.forEach((entry) => {
            byId[entry.id] = entry.name;
          });
          namesByCategory[category] = byId;
        });
        setEnglishNamesByCategory(namesByCategory);
      })
      .catch((err) => console.error("Error fetching category lists:", err.message));
  }, []);

  const englishName = (submission) =>
    englishNamesByCategory[submission.target_table]?.[submission.target_id] ||
    `#${submission.target_id}`;

  const loadPending = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}/api/translations/pending`,
        { headers: { Authorization: `Bearer ${token.access_token}` } }
      );
      const data = response.ok ? await response.json() : [];
      setPending(data);
      const initialEdits = {};
      data.forEach((submission) => {
        initialEdits[submission.id] = submission.final_name || submission.name;
      });
      setEditValues(initialEdits);
    } catch (error) {
      console.error("Error loading pending translations:", error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPending();
  }, [token]);

  const languageName = (languageId) =>
    languages.find((l) => l.id === languageId)?.name || `Language #${languageId}`;

  const handleApprove = async (submission) => {
    setActionError("");
    try {
      const editedValue = editValues[submission.id];
      if (editedValue !== (submission.final_name || submission.name)) {
        const editResponse = await fetch(
          `${process.env.REACT_APP_API_HOST}/api/translations/${submission.id}/edit`,
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${token.access_token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              final_name: editedValue,
              final_description: null,
            }),
          }
        );
        if (!editResponse.ok) {
          throw new Error("Could not save the edited name");
        }
      }

      const approveResponse = await fetch(
        `${process.env.REACT_APP_API_HOST}/api/translations/${submission.id}/approve`,
        {
          method: "PUT",
          headers: { Authorization: `Bearer ${token.access_token}` },
        }
      );
      if (!approveResponse.ok) {
        throw new Error("Could not approve - the target row may no longer exist");
      }
      await loadPending();
    } catch (error) {
      setActionError(error.message);
    }
  };

  const handleApproveAll = async (submittedByAccountId) => {
    setActionError("");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}/api/translations/approve-all?submitted_by=${submittedByAccountId}`,
        {
          method: "PUT",
          headers: { Authorization: `Bearer ${token.access_token}` },
        }
      );
      if (!response.ok) {
        throw new Error("Could not approve all submissions from this person");
      }
      await loadPending();
    } catch (error) {
      setActionError(error.message);
    }
  };

  const handleReject = async (submission) => {
    setActionError("");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}/api/translations/${submission.id}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token.access_token}` },
        }
      );
      if (!response.ok) {
        throw new Error("Could not reject submission");
      }
      await loadPending();
    } catch (error) {
      setActionError(error.message);
    }
  };

  if (!token) {
    return null;
  }

  const pendingCountBySubmitter = {};
  pending.forEach((submission) => {
    pendingCountBySubmitter[submission.submitted_by_account_id] =
      (pendingCountBySubmitter[submission.submitted_by_account_id] || 0) + 1;
  });

  return (
    <div className="text-white">
      <Nav />
      <div className="container mx-auto p-4">
        <h1 className="text-2xl font-bold mb-4">Review Pending Translations</h1>

        {loading ? (
          <p>Loading...</p>
        ) : pending.length === 0 ? (
          <p>No pending submissions right now.</p>
        ) : (
          <>
            <div className="mb-4">
              <h2 className="font-bold mb-2">Bulk approve by submitter</h2>
              {Object.entries(pendingCountBySubmitter).map(([accountId, count]) => (
                <div key={accountId} className="mb-1">
                  <button
                    onClick={() => handleApproveAll(accountId)}
                    className="mr-2 p-1 border rounded"
                  >
                    Approve All
                  </button>
                  {accountsById[accountId] || `#${accountId}`} ({count} pending)
                </div>
              ))}
            </div>
          <table className="min-w-full divide-y divide-gray-200 text-left">
            <thead>
              <tr>
                <th className="pr-4">Category</th>
                <th className="pr-4">English Name</th>
                <th className="pr-4">Language</th>
                <th className="pr-4">Submitted Name</th>
                <th className="pr-4">Submitted By</th>
                <th className="pr-4">Submitted At</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {pending.map((submission) => (
                <tr key={submission.id}>
                  <td className="pr-4 py-1">
                    {CATEGORY_LABELS[submission.target_table] || submission.target_table}
                  </td>
                  <td className="pr-4 py-1">{englishName(submission)}</td>
                  <td className="pr-4 py-1">{languageName(submission.language_id)}</td>
                  <td className="pr-4 py-1">
                    <input
                      type="text"
                      value={editValues[submission.id] || ""}
                      onChange={(e) =>
                        setEditValues({ ...editValues, [submission.id]: e.target.value })
                      }
                      className="p-1 border rounded text-black"
                    />
                  </td>
                  <td className="pr-4 py-1">
                    {accountsById[submission.submitted_by_account_id] ||
                      `#${submission.submitted_by_account_id}`}
                  </td>
                  <td className="pr-4 py-1">
                    {new Date(submission.submitted_at).toLocaleString()}
                  </td>
                  <td className="py-1">
                    <button
                      onClick={() => handleApprove(submission)}
                      className="mr-2 p-1 border rounded"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => handleReject(submission)}
                      className="p-1 border rounded"
                    >
                      Reject
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </>
        )}

        {actionError && <p className="text-red-500 mt-2">{actionError}</p>}
      </div>
    </div>
  );
}
